import { Alert, Box, Button, FormControlLabel, Switch, TextField, Typography } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import { BlockStatus, BlockStatusPayload } from "src/apis/upsell";
import { formatDateToBR } from "src/utils/date";

type BlockStatusFormProps = {
  title: string;
  description: string;
  status: BlockStatus | null;
  onSave: (payload: BlockStatusPayload) => Promise<void>;
};

const defaultPayload: BlockStatusPayload = {
  is_blocked: false,
  restricted_actions: [],
  status_message: { title: "", description: "" },
  internal_message: "",
};

const BlockStatusForm: FC<BlockStatusFormProps> = ({ title, description, status, onSave }) => {
  const [payload, setPayload] = useState<BlockStatusPayload>(defaultPayload);
  const [isSaving, setIsSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ type: "success" | "error"; message: string } | null>(null);

  useEffect(() => {
    if (!status) return;
    setPayload({
      is_blocked: status.is_blocked,
      restricted_actions: status.restricted_actions ?? [],
      status_message: status.status_message ?? { title: "", description: "" },
      internal_message: status.internal_message ?? "",
    });
  }, [status]);

  const restrictedActionsText = useMemo(
    () => payload.restricted_actions?.join(", ") ?? "",
    [payload.restricted_actions]
  );

  const handleSave = async () => {
    setIsSaving(true);
    setFeedback(null);
    try {
      await onSave(payload);
      setFeedback({ type: "success", message: "Configurações atualizadas com sucesso." });
    } catch (err: any) {
      setFeedback({ type: "error", message: err?.message || "Erro ao salvar bloqueio." });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box border={1} borderColor="divider" borderRadius={2} padding={2} display="flex" flexDirection="column" gap={2}>
      <Box>
        <Typography variant="h6">{title}</Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </Box>
      {feedback && <Alert severity={feedback.type}>{feedback.message}</Alert>}
      <FormControlLabel
        control={
          <Switch
            checked={payload.is_blocked}
            onChange={(e) => setPayload((prev) => ({ ...prev, is_blocked: e.target.checked }))}
          />
        }
        label={payload.is_blocked ? "Empresa bloqueada" : "Empresa ativa"}
      />
      <TextField
        label="Título da mensagem"
        value={payload.status_message?.title ?? ""}
        onChange={(e) =>
          setPayload((prev) => ({
            ...prev,
            status_message: { ...prev.status_message, title: e.target.value },
          }))
        }
        fullWidth
      />
      <TextField
        label="Descrição exibida para a empresa"
        value={payload.status_message?.description ?? ""}
        onChange={(e) =>
          setPayload((prev) => ({
            ...prev,
            status_message: { ...prev.status_message, description: e.target.value },
          }))
        }
        fullWidth
        multiline
        minRows={3}
      />
      <TextField
        label="Ações restritas"
        value={restrictedActionsText}
        onChange={(e) =>
          setPayload((prev) => ({
            ...prev,
            restricted_actions: e.target.value
              .split(",")
              .map((action) => action.trim())
              .filter((action) => action.length > 0),
          }))
        }
        helperText="Separe as ações por vírgula. Ex: create_request, invite_collaborator"
      />
      <TextField
        label="Mensagem interna (visível para o time EezyCare)"
        value={payload.internal_message ?? ""}
        onChange={(e) => setPayload((prev) => ({ ...prev, internal_message: e.target.value }))}
        multiline
        minRows={2}
      />
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="caption" color="text.secondary">
          Última atualização {status?.updated_at ? formatDateToBR(status.updated_at) : "ainda não registrada"}
        </Typography>
        <Button variant="contained" onClick={handleSave} disabled={isSaving}>
          {isSaving ? "Salvando..." : "Salvar"}
        </Button>
      </Box>
    </Box>
  );
};

export default BlockStatusForm;
